// frontend/src/services/authenticationService.js

import { apiUtils } from '../utils/apiUtils';
import logger from '../utils/logger';
import environmentConfig from '../config/environmentConfig';
import { decodeJWT, extractFederationMode, getUserFromToken } from '../utils/jwtUtils';

const authLogger = logger.withComponent('authentication-service');

/**
 * Frontend Authentication Service
 * Handles secure cookie-based authentication and session management
 */
class AuthenticationService {
  constructor() {
    this.currentUser = null;
    this.authMethod = 'none';
    this.listeners = new Set();
    this.refreshTimer = null;

    // Configuration
    this.config = {
      refreshThreshold: 5 * 60 * 1000, // Refresh token 5 minutes before expiry
      maxRetries: 3,
      retryDelay: 1000,
    };

    // Initialize service
    this.initialize();
  }

  /**
   * Initialize the authentication service
   */
  async initialize() {
    try {
      // Skip initialization for public and patient portal pages
      const currentPath = window.location.pathname;
      const publicPaths = [
        '/',
        '/login',
        '/asc/login',
        '/oauth/callback',
        '/patient',
        // '/patient/login', - deprecated, using unified /login
        '/patient/register',
        '/patient/forgot-password',
      ];
      const isPublicPath =
        publicPaths.includes(currentPath) ||
        currentPath.startsWith('/oauth/callback') ||
        currentPath.startsWith('/patient/');

      if (isPublicPath) {
        authLogger.info('Skipping auth initialization for public path', { path: currentPath });
        return;
      }

      // Check for existing authentication
      await this.checkAuthStatus();

      // Set up automatic token refresh
      this.setupTokenRefresh();

      // Listen for visibility changes to refresh auth
      document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

      // Listen for storage changes for cross-tab logout
      window.addEventListener('storage', this.handleStorageChange.bind(this));

      // Listen for API unauthorized events
      window.addEventListener('auth:unauthorized', this.handleUnauthorized.bind(this));

      authLogger.info('Authentication service initialized');
    } catch (error) {
      authLogger.error('Failed to initialize authentication service', { error: error.message });
    }
  }

  /**
   * Login with credentials
   */
  async login(credentials, options = {}) {
    try {
      const { provider = 'jwt', rememberMe = false } = options;

      authLogger.info('Attempting login', {
        username: credentials.username,
        provider,
      });

      // Use ASC login endpoint if in ASC mode
      const loginEndpoint = options.ascMode ? '/api/asc/auth/login' : '/api/auth/login';

      // Determine the mode based on options, stored appMode, or current environment
      let mode = 'legacy'; // Default mode

      // First check if there's a stored appMode from the homepage selection
      let storedAppMode = localStorage.getItem('appMode');
      
      // Clean up if appMode was incorrectly stored as an object
      if (storedAppMode && storedAppMode.startsWith('{')) {
        try {
          const parsed = JSON.parse(storedAppMode);
          storedAppMode = parsed.mode || parsed.id || 'hospital';
          localStorage.setItem('appMode', storedAppMode);
        } catch (e) {
          storedAppMode = 'hospital';
          localStorage.setItem('appMode', storedAppMode);
        }
      }

      if (options.ascMode) {
        mode = 'asc';
      } else if (
        options.hospitalMode ||
        storedAppMode === 'hospital' ||
        window.location.hostname === 'virtualpat.ai'
      ) {
        // Use hospital mode if explicitly set, stored from homepage, or on production
        mode = 'hospital';
      } else if (storedAppMode === 'asc') {
        mode = 'asc';
      }

      console.log('[AuthService] Attempting login with:', {
        endpoint: loginEndpoint,
        username: credentials.username,
        mode,
        storedAppMode: typeof storedAppMode === 'object' ? JSON.stringify(storedAppMode) : storedAppMode,
        hostname: window.location.hostname,
      });

      const response = await apiUtils.post(loginEndpoint, {
        ...credentials,
        provider,
        rememberMe,
        mode,
      });

      console.log('[AuthService] Login response:', {
        success: response.success,
        hasData: !!response.data,
        dataKeys: response.data ? Object.keys(response.data) : [],
      });

      if (response.success) {
        // Handle the response structure from backend
        // The backend returns {success: true, data: {user: {...}, token: '...'}}
        // ApiUtils wraps it as {success: true, data: <backend_response>}
        // So we need to access response.data.data to get the actual login data
        const loginData = response.data.data || response.data;

        // Store appMode in localStorage before handling login success
        // Only update appMode if we have a specific mode to set
        if (options.ascMode || mode === 'asc') {
          localStorage.setItem('appMode', 'asc');
        } else if (mode === 'hospital') {
          localStorage.setItem('appMode', 'hospital');
        }
        // Don't overwrite with 'standard' if there's already a stored mode

        await this.handleLoginSuccess(loginData, provider);

        authLogger.info('Login successful', {
          username: credentials.username,
          provider,
          authMethod: this.authMethod,
        });

        return {
          success: true,
          user: this.currentUser,
          method: this.authMethod,
        };
      }
      console.error('[AuthService] Login failed - response not successful:', response);
      // Extract error message properly from response structure
      const errorMessage = response.error?.message || response.error || 'Invalid username or password';
      throw new Error(errorMessage);
    } catch (error) {
      authLogger.error('Login failed', {
        username: credentials.username,
        error: error.message,
      });

      console.error('[AuthService] Login error details:', {
        message: error.message,
        responseData: error.response?.data,
        status: error.response?.status,
      });

      // Extract error message from various possible locations
      let errorMessage = 'Invalid username or password';
      
      if (error.response?.data?.error) {
        // Backend returns error as object with message property
        if (typeof error.response.data.error === 'object' && error.response.data.error.message) {
          errorMessage = error.response.data.error.message;
        } else if (typeof error.response.data.error === 'string') {
          errorMessage = error.response.data.error;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message && error.message !== '[object Object]') {
        errorMessage = error.message;
      }

      throw new Error(errorMessage);
    }
  }

  /**
   * Logout
   */
  async logout() {
    try {
      authLogger.info('Attempting logout', {
        userId: this.currentUser?.id,
        mode: this.currentUser?.mode,
      });

      // Call logout endpoint to invalidate server-side session
      // Use ASC logout endpoint if user is in ASC mode
      const logoutEndpoint =
        this.currentUser?.mode === 'asc' ? '/api/asc/auth/logout' : '/api/auth/logout';

      await apiUtils.post(logoutEndpoint);

      await this.handleLogoutSuccess();

      authLogger.info('Logout successful');

      return { success: true };
    } catch (error) {
      authLogger.error('Logout failed', { error: error.message });

      // Force local logout even if server call fails
      await this.handleLogoutSuccess();

      throw error;
    }
  }

  /**
   * Check current authentication status
   */
  async checkAuthStatus() {
    try {
      // Don't skip auth check for patient portal - they need authentication too!

      // First check if we have a token and extract federation mode from it
      const token = this.getAccessToken() || localStorage.getItem('token');
      let federationMode = null;

      if (token) {
        federationMode = extractFederationMode(token);
        authLogger.debug('Federation mode from token:', federationMode);
      }

      // Get auth endpoint from config, fallback to standard endpoint
      const config = environmentConfig.getConfig();
      const appMode = federationMode || localStorage.getItem('appMode');
      const authEndpoint =
        appMode === 'asc' ? '/api/asc/auth/me' : config.authEndpoints?.me || '/api/auth/me';

      authLogger.debug(`Checking auth status with endpoint: ${authEndpoint}`);

      console.log(`[AuthService] Checking auth at endpoint: ${authEndpoint}`);
      console.log('[AuthService] Token exists:', !!token);
      
      // Silently check auth status without logging errors for 401s
      let response = await apiUtils.get(authEndpoint).catch(error => {
        console.log('[AuthService] Auth check error:', error.response?.status, error.message);
        if (error.response?.status === 401) {
          // Not authenticated (or expired) — handled below with silent refresh retry
          return { success: false, data: null, status: 401 };
        }
        return { success: false, data: null, status: error.response?.status };
      });

      console.log('[AuthService] Auth check response:', {
        success: response?.success,
        hasData: !!response?.data,
        dataKeys: response?.data ? Object.keys(response.data) : []
      });
      authLogger.debug('Auth status check response:', response);

      if (response.success && response.data) {
        // Handle response structure from backend
        const authData = response.data;

        authLogger.debug('Auth data extracted:', authData);

        // Check if we have user data (backend might return {user: {...}} or just {success: false})
        // The /api/auth/me endpoint returns user data directly in authData
        if (authData.user || authData.id) {
          const userData = authData.user || authData;

          // Add federation info from token if not already present
          if (federationMode && !userData.federation) {
            userData.federation = federationMode;
            userData.mode = federationMode;
          }

          this.setUser(userData);
          this.authMethod = authData.authMethod || userData.authMethod || 'jwt';

          authLogger.debug('Auth status checked - authenticated', {
            userId: this.currentUser.id,
            authMethod: this.authMethod,
            federation: this.currentUser.federation,
          });

          return {
            authenticated: true,
            user: this.currentUser,
            method: this.authMethod,
          };
        }
      }

      // If unauthenticated, attempt a one-time silent refresh and retry auth/me
      const unauthenticated = !response?.success || !response?.data;
      if (unauthenticated) {
        try {
          console.log('[AuthService] Attempting silent refresh...');
          const refreshResp = await apiUtils.post('/api/auth/refresh', {});
          const newToken = refreshResp?.headers?.['x-new-access-token'] ||
                           refreshResp?.headers?.['X-New-Access-Token'] ||
                           refreshResp?.data?.data?.token || refreshResp?.data?.token;
          if (newToken) {
            try {
              this.setAccessToken(newToken);
              localStorage.setItem('token', newToken);
            } catch {}
          }
          // Retry auth/me
          response = await apiUtils.get(authEndpoint).catch(() => ({ success: false }));
          if (response.success && response.data && (response.data.user || response.data.id)) {
            const userData = response.data.user || response.data;
            if (federationMode && !userData.federation) {
              userData.federation = federationMode;
              userData.mode = federationMode;
            }
            this.setUser(userData);
            this.authMethod = response.data.authMethod || userData.authMethod || 'jwt';
            return { authenticated: true, user: this.currentUser, method: this.authMethod };
          }
        } catch (refreshErr) {
          console.log('[AuthService] Silent refresh failed:', refreshErr?.message);
        }

        authLogger.debug(
          'Auth status check - not authenticated after refresh attempt'
        );
        this.clearUser();
        return { authenticated: false };
      }
    } catch (error) {
      authLogger.debug('Auth status check failed in catch block', { error: error.message });
      this.clearUser();
      return { authenticated: false };
    }
  }

  /**
   * Refresh authentication tokens
   */
  async refreshAuth() {
    try {
      authLogger.debug('Refreshing authentication');

      const response = await apiUtils.post('/api/auth/refresh');

      if (response.success) {
        // Update access token if provided in response headers
        const newToken = response.headers?.['x-new-access-token'];
        if (newToken) {
          this.setAccessToken(newToken);
        }

        // Update user data if provided
        if (response.data?.user) {
          this.setUser(response.data.user);
        }

        authLogger.debug('Authentication refreshed successfully');
        return { success: true };
      }
      throw new Error(response.error || 'Token refresh failed');
    } catch (error) {
      authLogger.error('Token refresh failed', { error: error.message });

      // If refresh fails, clear authentication and redirect to login
      await this.handleAuthFailure();
      throw error;
    }
  }

  /**
   * Get current user
   */
  getCurrentUser() {
    return this.currentUser;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return this.currentUser !== null;
  }

  /**
   * Get current authentication method
   */
  getAuthMethod() {
    return this.authMethod;
  }

  /**
   * Add authentication state listener
   */
  addListener(callback) {
    this.listeners.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Handle successful login
   */
  async handleLoginSuccess(data, provider) {
    const { user, token, accessToken, sessionId, mode } = data;

    // Store access token first so we can extract federation info
    // Backend might send 'token' or 'accessToken'
    const authToken = token || accessToken;
    if (authToken) {
      this.setAccessToken(authToken);
      // Also store in the legacy 'token' key for backward compatibility
      localStorage.setItem('token', authToken);

      // Extract federation mode from JWT token
      const federationMode = extractFederationMode(authToken);
      if (federationMode) {
        // Add federation info to user object
        if (user) {
          user.federation = federationMode;
          user.mode = federationMode;
        }

        // Store the mode
        localStorage.setItem('appMode', federationMode);
        // Also update environment mode if it's hospital
        if (federationMode === 'hospital') {
          localStorage.setItem('environmentMode', 'hospital');
        }
      }
    }

    // Store user in localStorage for backward compatibility
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }

    // Store the mode from the response (fallback if not in JWT)
    if (mode && !user?.federation) {
      localStorage.setItem('appMode', mode);
      // Also update environment mode if it's hospital
      if (mode === 'hospital') {
        localStorage.setItem('environmentMode', 'hospital');
      }
    }

    this.setUser(user);
    this.authMethod = provider;

    // Store session information
    if (sessionId) {
      localStorage.setItem('sessionId', sessionId);
    }

    // Store last activity time
    localStorage.setItem('lastActivity', Date.now().toString());

    // Set up token refresh
    this.setupTokenRefresh();

    // Notify listeners
    this.notifyListeners({
      type: 'login',
      user: this.currentUser,
      method: this.authMethod,
    });
  }

  /**
   * Handle successful logout
   */
  async handleLogoutSuccess() {
    this.clearUser();
    this.clearTokens();
    this.clearRefreshTimer();

    // Clear session storage
    localStorage.removeItem('sessionId');
    localStorage.removeItem('appMode'); // Clear app mode on logout
    sessionStorage.clear();

    // Notify listeners
    this.notifyListeners({
      type: 'logout',
    });
  }

  /**
   * Handle authentication failure
   */
  async handleAuthFailure() {
    authLogger.warn('Authentication failure detected');

    this.clearUser();
    this.clearTokens();
    this.clearRefreshTimer();

    // Notify listeners
    this.notifyListeners({
      type: 'auth_failure',
    });
  }

  /**
   * Set current user
   */
  setUser(user) {
    // First check if user object already has federation/mode from JWT
    let mode = user?.federation || user?.mode;

    // If not, try to extract from stored token
    if (!mode) {
      const token = this.getAccessToken() || localStorage.getItem('token');
      if (token) {
        mode = extractFederationMode(token);
      }
    }

    // Fall back to localStorage appMode or role-based detection
    if (!mode) {
      const appMode = localStorage.getItem('appMode');
      const isAscRole =
        user?.role &&
        [
          'ASC_ADMINISTRATOR',
          'ASC_CLINICAL_DIRECTOR',
          'ASC_SCHEDULER',
          'ASC_SURGEON',
          'ASC_NURSE',
          'ASC_FRONT_DESK',
        ].includes(user.role);

      // Determine the mode - prioritize appMode from localStorage
      mode = appMode;
      if (!mode) {
        // If no appMode stored, determine from role or environment
        if (isAscRole) {
          mode = 'asc';
        } else if (window.location.hostname === 'virtualpat.ai') {
          // On production, default to hospital mode
          mode = 'hospital';
        } else {
          mode = 'standard';
        }
      }
    }

    this.currentUser = {
      ...user,
      mode,
      federation: mode, // Ensure federation field is also set
    };
  }

  /**
   * Update current user data
   */
  updateCurrentUser(userData) {
    if (!this.currentUser) {
      authLogger.warn('Cannot update user - no user is currently authenticated');
      return;
    }

    // Merge the new user data with existing user data
    this.setUser({
      ...this.currentUser,
      ...userData,
    });

    // Notify listeners of the update
    this.notifyListeners({
      type: 'userUpdate',
      user: this.currentUser,
      method: this.authMethod,
    });

    authLogger.info('User data updated', {
      userId: this.currentUser.id,
      updatedFields: Object.keys(userData),
    });
  }

  /**
   * Clear current user
   */
  clearUser() {
    this.currentUser = null;
    this.authMethod = 'none';
    localStorage.removeItem('user');
  }

  /**
   * Set access token for API calls
   */
  setAccessToken(token) {
    // Store in localStorage for cross-tab persistence
    // Combined with HTTP-only cookies for security
    localStorage.setItem('_at', token);
  }

  /**
   * Get access token
   */
  getAccessToken() {
    return localStorage.getItem('_at');
  }

  /**
   * Clear all tokens
   */
  clearTokens() {
    localStorage.removeItem('_at');
    localStorage.removeItem('token');
  }

  /**
   * Setup automatic token refresh
   */
  setupTokenRefresh() {
    this.clearRefreshTimer();
    // No periodic refresh (no polling). Token refresh happens on-demand:
    // - 401 interceptor attempts a one-time refresh + retry
    // - tab focus triggers an auth status check
  }

  /**
   * Clear refresh timer
   */
  clearRefreshTimer() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Handle visibility change (tab focus)
   */
  async handleVisibilityChange() {
    // Skip for patient portal pages
    const currentPath = window.location.pathname;
    const isPatientPortal = currentPath.startsWith('/patient/');

    if (isPatientPortal) {
      return;
    }

    if (!document.hidden && this.isAuthenticated()) {
      // Tab became visible, check auth status
      try {
        await this.checkAuthStatus();
      } catch (error) {
        authLogger.debug('Auth status check on visibility change failed', { error: error.message });
      }
    }
  }

  /**
   * Handle storage changes (cross-tab communication)
   */
  handleStorageChange(event) {
    if (event.key === 'logout' && event.newValue) {
      // Another tab logged out
      this.handleLogoutSuccess();
    }
  }

  /**
   * Handle unauthorized API responses
   */
  async handleUnauthorized() {
    // Be defensive: verify auth with a silent check before clearing state
    try {
      authLogger.warn('Unauthorized event received. Verifying session before logout...');
      const status = await this.checkAuthStatus();
      if (status && status.authenticated) {
        authLogger.info('Session still valid after verification; ignoring unauthorized event');
        return;
      }
    } catch (e) {
      authLogger.debug('Verification after unauthorized failed', { error: e?.message });
    }
    authLogger.warn('No valid session; clearing authentication state');
    this.handleLogoutSuccess();
  }

  /**
   * Notify all listeners of authentication state changes
   */
  notifyListeners(event) {
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        authLogger.error('Auth listener error', { error: error.message });
      }
    });
  }

  /**
   * OAuth login initiation
   */
  async initiateOAuthLogin(provider, options = {}) {
    try {
      const response = await apiUtils.get(`/api/auth/oauth/${provider}/authorize`, {
        params: options,
      });

      if (response.success && response.data.authUrl) {
        // Redirect to OAuth provider
        window.location.href = response.data.authUrl;
        return { success: true };
      }

      throw new Error(response.error || 'OAuth initialization failed');
    } catch (error) {
      authLogger.error('OAuth login initiation failed', {
        provider,
        error: error.message,
      });
      throw error;
    }
  }

  /**
   * Handle OAuth callback
   */
  async handleOAuthCallback(provider, params) {
    try {
      const response = await apiUtils.post(`/api/auth/oauth/${provider}/callback`, params);

      if (response.success) {
        await this.handleLoginSuccess(response.data, 'oauth');
        return { success: true, user: this.currentUser };
      }

      throw new Error(response.error || 'OAuth callback failed');
    } catch (error) {
      authLogger.error('OAuth callback failed', {
        provider,
        error: error.message,
      });
      throw error;
    }
  }

  /**
   * SAML login initiation
   */
  async initiateSAMLLogin(provider, options = {}) {
    try {
      const response = await apiUtils.get(`/api/auth/saml/${provider}/login`, {
        params: options,
      });

      if (response.success && response.data.samlRequest) {
        // This would typically redirect to SAML IdP
        return { success: true, samlRequest: response.data.samlRequest };
      }

      throw new Error(response.error || 'SAML initialization failed');
    } catch (error) {
      authLogger.error('SAML login initiation failed', {
        provider,
        error: error.message,
      });
      throw error;
    }
  }

  /**
   * Handle SAML response
   */
  async handleSAMLResponse(provider, samlResponse) {
    try {
      const response = await apiUtils.post(`/api/auth/saml/${provider}/acs`, {
        SAMLResponse: samlResponse,
      });

      if (response.success) {
        await this.handleLoginSuccess(response.data, 'saml');
        return { success: true, user: this.currentUser };
      }

      throw new Error(response.error || 'SAML response handling failed');
    } catch (error) {
      authLogger.error('SAML response handling failed', {
        provider,
        error: error.message,
      });
      throw error;
    }
  }
}

// Create and export singleton instance
const authenticationService = new AuthenticationService();

// Export to window for debugging (only in development)
if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
  window.authenticationService = authenticationService;
}

export default authenticationService;
